/**
 * Baut die Rümpfe von `POST /due-dates` und `PATCH /due-dates/{id}` aus dem
 * Formularzustand des Sheets `duedate-sheet` — dieselbe Aufteilung wie
 * `features/tasks/task-form.ts`: `PATCH` trägt nur die geänderten Felder,
 * ein geleertes Feld wird direkt als `null` eingetragen (kein `clear`, das
 * ist die iOS-Ergänzung).
 *
 * `recurrenceOption` ist eine reine Bedienhilfe für die Auswahl im Formular
 * — welches Feld sich öffnet (`duedate-recurrence-months` bei „anderes
 * Intervall") —, dieselbe Bauform wie `_RECURRENCE_OPTIONS` in
 * `due_dates_service.py`. Der tatsächlich gesendete Wert ist
 * `recurrence_months` selbst, aus `resolveRecurrenceMonths()` gebildet.
 */

import type { DueDateCreate, DueDateRow, DueDateTemplate, DueDateUpdate } from './api'

export interface DueDateFormValues {
  readonly title: string
  readonly dueOn: string
  /** `'none' | '1' | '12' | '24' | '36' | 'custom'` — dieselben `value`s wie
   * `GET /due-dates/recurrences`. */
  readonly recurrenceOption: string
  /** Nur benutzt, wenn `recurrenceOption === 'custom'`. */
  readonly recurrenceMonthsCustom: string
  readonly leadDays: string
  /** `''` heißt „ohne Zuständigen" — der Regelfall. */
  readonly taskAssigneeId: string
  /** Freier Text, ungetrimmt im Formular — `buildDueDateCreateBody`/
   * `buildDueDatePatchBody` trimmen erst beim Senden (Spec 17, „Notizen am
   * Termin"). `''` heißt „keine Notizen". */
  readonly notes: string
}

/** Nur noch für die **Rückrichtung** gebraucht (`recurrenceOptionFor`: eine
 * Monatszahl kennt keine Stufe). Für das **Senden** (`resolveRecurrenceMonths`)
 * reicht die Options-Kennung selbst, siehe dort — eine hartkodierte Kopie an
 * dieser Stelle hätte eine sechste Stufe am Server still auf `null`
 * abgebildet, dieselbe Ausfallart wie K5. Bleibt hier hartkodiert: Träfe die
 * Rückrichtung eine unbekannte Stufe nicht, zeigt sie „anderes Intervall" mit
 * der Zahl im freien Feld — sichtbar korrekt, nur ohne eigenen Namen, also
 * kein stiller Verlust wie beim Senden. Volle Herleitung aus
 * `GET /due-dates/recurrences` bräuchte die Anfrage in zwei reinen
 * Funktionen ohne Zugriff darauf — Umbau ohne Gegenwert für diesen Fall. */
const FIXED_RECURRENCE_MONTHS: Record<string, number> = {
  '1': 1,
  '12': 12,
  '24': 24,
  '36': 36,
}

/** `recurrenceOption`/`recurrenceMonthsCustom` → `recurrence_months`, der
 * tatsächliche Rumpfwert. Für eine feste Stufe **ist** der Options-Wert
 * bereits die Monatszahl als Text (`GET /due-dates/recurrences`:
 * `'1'|'12'|'24'|'36'`) — direkt gelesen, keine eigene Kopie der
 * Server-Werte. `duedate-sheet` ruft diese Funktion erst auf, nachdem
 * `hasInvalidRecurrenceMonths` geprüft hat: „anderes Intervall" ohne
 * gültige Zahl wird als Feldfehler an `duedate-recurrence-months` behandelt,
 * nicht mehr still zu `null` — der Server nimmt `null` als „einmalig" an,
 * es kommt also **kein** 422 (K5). */
export function resolveRecurrenceMonths(
  form: Pick<DueDateFormValues, 'recurrenceOption' | 'recurrenceMonthsCustom'>,
): number | null {
  if (form.recurrenceOption === 'none') return null
  if (form.recurrenceOption === 'custom') {
    const trimmed = form.recurrenceMonthsCustom.trim()
    if (trimmed === '') return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  const parsed = Number(form.recurrenceOption)
  return Number.isFinite(parsed) ? parsed : null
}

/** `true`, wenn „anderes Intervall" ohne eine gültige Zahl im freien Feld
 * gewählt ist — der Fall, den `resolveRecurrenceMonths` früher still zu
 * `null` (= „einmalig") machte (K5). `duedate-sheet` prüft das vor dem
 * Senden und zeigt statt dessen einen Feldfehler, es wird nichts gesendet. */
export function hasInvalidRecurrenceMonths(
  form: Pick<DueDateFormValues, 'recurrenceOption' | 'recurrenceMonthsCustom'>,
): boolean {
  if (form.recurrenceOption !== 'custom') return false
  const trimmed = form.recurrenceMonthsCustom.trim()
  if (trimmed === '') return true
  return !Number.isFinite(Number(trimmed))
}

/** Meldung für den Feldfehler aus `hasInvalidRecurrenceMonths`. */
export const RECURRENCE_MONTHS_REQUIRED_MESSAGE = 'Bitte eine Zahl angeben.'

/** Die Umkehrung von `resolveRecurrenceMonths` — welche Option im Formular
 * beginnt vorbelegt zu sein, wenn ein vorhandener `recurrence_months`-Wert
 * (aus einem Termin oder einer Vorlage) ankommt. Ein Wert außerhalb der vier
 * festen Stufen (z. B. 48 bei der Vorlage „DGUV V3 ortsfest") ist
 * `'custom'`. */
export function recurrenceOptionFor(months: number | null): string {
  if (months === null) return 'none'
  const match = Object.entries(FIXED_RECURRENCE_MONTHS).find(([, value]) => value === months)
  return match?.[0] ?? 'custom'
}

/** Text für `duedate-template-note` (Spec, „Vorlagenauswahl", `FACHREGELN.md`
 * Regel 2: „ausdrücklich als Vorschlag zu kennzeichnen"). */
export function templateNoteText(note: string | null): string {
  const base = 'Vorschlag — bitte prüfen.'
  return note === null ? base : `${base} ${note}`
}

/** AK-410: Die Auswahl einer Vorlage füllt **nur** Bezeichnung, Wiederholung
 * und Vorlauf vor — als reine Funktion, damit die Vorbelegung selbst geprüft
 * werden kann, ohne das Sheet zu rendern (`duedate-sheet` mischt das
 * Ergebnis in den bestehenden Formularzustand, überschreibt also gezielt nur
 * diese drei Felder). */
export function templateFormPatch(
  template: DueDateTemplate,
): Pick<DueDateFormValues, 'title' | 'recurrenceOption' | 'recurrenceMonthsCustom' | 'leadDays'> {
  const recurrenceOption = recurrenceOptionFor(template.recurrence_months)
  return {
    title: template.title,
    recurrenceOption,
    recurrenceMonthsCustom: recurrenceOption === 'custom' ? String(template.recurrence_months) : '',
    leadDays: String(template.lead_days),
  }
}

/** AK-568: „beim erneuten Öffnen des Sheets steht die gewählte Person darin"
 * — die Umkehrung von `buildDueDatePatchBody` für `task_assignee_id`, als
 * reine Funktion für denselben Grund wie `templateFormPatch`. Trägt auch die
 * übrigen Felder, damit `duedate-sheet` beim Ändern mit genau diesem
 * Formularzustand beginnt. */
export function deriveEditForm(dueDate: DueDateRow): DueDateFormValues {
  const recurrenceOption = recurrenceOptionFor(dueDate.recurrence_months)
  return {
    title: dueDate.title,
    dueOn: dueDate.due_on,
    recurrenceOption,
    recurrenceMonthsCustom: recurrenceOption === 'custom' ? String(dueDate.recurrence_months ?? '') : '',
    leadDays: String(dueDate.lead_days),
    taskAssigneeId: dueDate.task_assignee?.id ?? '',
    notes: dueDate.notes ?? '',
  }
}

/** AK-568, wörtlich: „Beschriftung ‚Zuständig für die Aufgabe' und Hilfstext
 * ‚Der Termin selbst hat keine zuständige Person.'" — als Konstanten, damit
 * ein Test die tatsächlich angezeigte Formulierung gegen den Wortlaut der
 * Spec hält, ohne das Sheet zu rendern. */
export const TASK_ASSIGNEE_LABEL = 'Zuständig für die Aufgabe'
export const TASK_ASSIGNEE_HINT =
  'Der Termin selbst hat keine zuständige Person. Bleibt das Feld leer, entsteht die Aufgabe ohne Zuständigen.'

export function buildDueDateCreateBody(
  subjectId: string,
  templateKey: string | null,
  form: DueDateFormValues,
): DueDateCreate {
  const trimmedNotes = form.notes.trim()
  return {
    subject_id: subjectId,
    title: form.title.trim(),
    due_on: form.dueOn,
    lead_days: Number(form.leadDays),
    recurrence_months: resolveRecurrenceMonths(form),
    task_assignee_id: form.taskAssigneeId === '' ? null : form.taskAssigneeId,
    template_key: templateKey,
    notes: trimmedNotes === '' ? null : trimmedNotes,
  }
}

export function buildDueDatePatchBody(form: DueDateFormValues, initial: DueDateFormValues): DueDateUpdate {
  const body: DueDateUpdate = {}

  const title = form.title.trim()
  if (title !== initial.title.trim()) body.title = title

  if (form.dueOn !== initial.dueOn) body.due_on = form.dueOn

  const recurrenceChanged =
    form.recurrenceOption !== initial.recurrenceOption ||
    (form.recurrenceOption === 'custom' && form.recurrenceMonthsCustom !== initial.recurrenceMonthsCustom)
  if (recurrenceChanged) body.recurrence_months = resolveRecurrenceMonths(form)

  if (form.leadDays !== initial.leadDays) body.lead_days = Number(form.leadDays)

  if (form.taskAssigneeId !== initial.taskAssigneeId) {
    body.task_assignee_id = form.taskAssigneeId === '' ? null : form.taskAssigneeId
  }

  const trimmedNotes = form.notes.trim()
  if (trimmedNotes !== initial.notes.trim()) body.notes = trimmedNotes === '' ? null : trimmedNotes

  return body
}
